// Iter65 — Manual CFM-CC-SHARES Scanner Panel.
//
// Read-only UI that lets the user POST a single JSON body to the
// Iter64 backend scanner:
//   POST /api/forge/toolbox/scanner/cfm-shares-preview
// One backend call per click. No auto-fire on load, no multi-endpoint
// fanout, no broker, account, position, journal, DB, or live market
// fetch.
//
// Inventory-management mirror of the Iter58 (BPCS), Iter60 (BCCS) and
// Iter63 (Iron Condor) panels — strictly isolated, never references
// any other module endpoint. Visual identity: cyan/teal to
// distinguish from BPCS (blue), BCCS (red), Wheel (green) and
// Iron Condor (amber).

import React, { useState } from "react";
import axios from "axios";
import {
  AlertTriangle, ChevronDown, ChevronUp, Play, Search,
  ShieldAlert, CheckCircle2, Eraser,
} from "lucide-react";
import { API } from "@/App";
import ScannerResultActions from "@/components/ScannerResultActions";
import {
  ReasonCodeChips,
  ScannerWarnings,
  ScannerErrorMessage,
} from "@/components/ScannerResultNormalization";
import FriendlyScannerForm from "@/components/FriendlyScannerForm";
import StrategyResultCard from "@/components/StrategyResultCard";

// Single allowed endpoint — hardcoded, never derived from input.
const SCANNER_ENDPOINT = "/forge/toolbox/scanner/cfm-shares-preview";

const DEFAULT_BODY = {
  watchlist: ["SPY"],
  payloads_by_symbol: {
    SPY: {
      spot_price: 500.0,
      wes: {
        val: 495.0, vah: 510.0, poc: 502.0,
        wes_p: 8, wes_c: 6, delta_VAL: 0.3,
        closed_bar_confirmed: true,
      },
      chain: [
        { type: "call", strike: 498.0, bid: 5.80, ask: 5.85,
          delta: 0.60, open_interest: 2000, volume: 120,
          expiration: "2026-06-21", dte: 14 },
        { type: "call", strike: 500.0, bid: 4.50, ask: 4.55,
          delta: 0.50, open_interest: 2500, volume: 150,
          expiration: "2026-06-21", dte: 14 },
        { type: "call", strike: 503.0, bid: 2.80, ask: 2.85,
          delta: 0.40, open_interest: 2200, volume: 130,
          expiration: "2026-06-21", dte: 14 },
      ],
      ivr: 35,
      account_context: {
        shares_owned: 100, share_basis: 498.0,
        total_call_credits: 2.50, minimum_exit_price: 500.0,
        allow_buy_write: true,
        assignment_policy: "accept_if_min_exit_met",
      },
    },
  },
};

const STATUS_BADGE = {
  DEFEND:           "border-[#FF3366]/40 bg-[#FF3366]/10 text-[#FF3366]",
  BUYBACK:          "border-[#FFB800]/40 bg-[#FFB800]/10 text-[#FFB800]",
  ROLL_OUT_UP:      "border-[#FFB800]/40 bg-[#FFB800]/10 text-[#FFB800]",
  ALLOW_ASSIGNMENT: "border-[#00B0FF]/40 bg-[#00B0FF]/10 text-[#00B0FF]",
  ENTER:            "border-[#00FF88]/40 bg-[#00FF88]/10 text-[#00FF88]",
  WATCH:            "border-[#FFB800]/40 bg-[#FFB800]/10 text-[#FFB800]",
  BLOCKED:          "border-[#FF3366]/40 bg-[#FF3366]/10 text-[#FF3366]",
  NO_TRADE:         "border-[#5F5F66]/40 bg-[#121216] text-[#8A8A93]",
  ERROR:            "border-[#FF3366]/40 bg-[#FF3366]/10 text-[#FF3366]",
};

const COUNT_KEYS = [
  "enter", "watch", "buyback", "roll_out_up",
  "allow_assignment", "defend", "blocked", "no_trade", "error",
];

export default function CFMSharesScannerPanel() {
  const [open, setOpen] = useState(false);
  const [body, setBody] = useState(JSON.stringify(DEFAULT_BODY, null, 2));
  const [parseErr, setParseErr] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [result, setResult] = useState(null);
  const [lastRequest, setLastRequest] = useState(null);

  async function runScanner() {
    setErr(null);
    setParseErr(null);
    let parsed;
    try {
      parsed = JSON.parse(body || "{}");
    } catch (e) {
      setParseErr(`Invalid JSON: ${e.message}`);
      return;
    }
    setLoading(true);
    try {
      const r = await axios.post(`${API}${SCANNER_ENDPOINT}`, parsed);
      setResult(r.data);
      setLastRequest(parsed);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Network error");
    } finally {
      setLoading(false);
    }
  }

  function resetBody() {
    setBody(JSON.stringify(DEFAULT_BODY, null, 2));
    setParseErr(null);
  }

  function clearResult() {
    setResult(null);
    setErr(null);
  }

  return (
    <div
      data-testid="cfm-shares-scanner-panel"
      className="mb-5 rounded-sm border border-[#26262B] bg-[#0A0A0E]"
    >
      <button
        data-testid="cfm-shares-scanner-toggle"
        onClick={() => setOpen(v => !v)}
        type="button"
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[#121216]"
      >
        <Search size={16} className="text-[#22D3CC]" />
        <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#E0E0E0]">
          Cash Flow Machine — Covered Call Shares Scanner
        </span>
        <span className="text-[9px] font-mono uppercase tracking-[0.12em] text-[#5F5F66]">
          · Iter64 backend · manual-run-per-click · inventory management · extrinsic income
        </span>
        <span className="ml-auto text-[#5F5F66]">
          {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </span>
      </button>

      {open && (
        <div className="border-t border-[#26262B]">
          {/* ── Safety banner ───────────────────────────────────── */}
          <div
            data-testid="cfm-shares-scanner-banner"
            className="px-4 py-2 border-b border-[#26262B] bg-[#22D3CC]/5 text-[#22D3CC] text-[10px] font-mono flex items-center gap-3 flex-wrap"
          >
            <ShieldAlert size={12} />
            <span data-testid="cfm-shares-scanner-banner-cfm-shares-only">
              CFM-CC-SHARES scanner only.
            </span>
            <span className="text-[#22D3CC]/50">·</span>
            <span data-testid="cfm-shares-scanner-banner-readonly">
              Read-only opportunity discovery.
            </span>
            <span className="text-[#22D3CC]/50">·</span>
            <span data-testid="cfm-shares-scanner-banner-no-execution">
              No trade execution.
            </span>
            <span className="text-[#22D3CC]/50">·</span>
            <span data-testid="cfm-shares-scanner-banner-prebuilt-payloads">
              Requires prebuilt payloads.
            </span>
            <span className="text-[#22D3CC]/50">·</span>
            <span data-testid="cfm-shares-scanner-banner-no-broker-account-fetch">
              No broker/account live-data fetch.
            </span>
            <span className="text-[#22D3CC]/50">·</span>
            <span data-testid="cfm-shares-scanner-banner-covered-calls-only">
              Covered calls against shares only.
            </span>
            <span className="text-[#22D3CC]/50">·</span>
            <span data-testid="cfm-shares-scanner-banner-assignment-aware">
              Assignment-aware, not assignment-forced.
            </span>
            <span className="text-[#22D3CC]/50">·</span>
            <span data-testid="cfm-shares-scanner-banner-premium-cushion">
              Premium cushion.
            </span>
            <span className="text-[#22D3CC]/50">·</span>
            <span data-testid="cfm-shares-scanner-banner-readonly-management-states">
              Read-only management states.
            </span>
            <span className="text-[#22D3CC]/50">·</span>
            <span data-testid="cfm-shares-scanner-banner-inventory-management">
              Inventory management · Basis reduction · Assignment-aware exit · Extrinsic income.
            </span>
          </div>

          {/* ── Body editor + run controls ───────────────────────── */}
          <div className="px-4 py-3 border-b border-[#26262B]">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-[#8A8A93]">
                Request body
              </span>
              <span className="text-[9px] font-mono text-[#5F5F66] truncate"
                    title={SCANNER_ENDPOINT}>
                {SCANNER_ENDPOINT}
              </span>
              <div className="ml-auto flex items-center gap-2">
                <button
                  type="button"
                  data-testid="cfm-shares-scanner-reset"
                  onClick={resetBody}
                  className="px-2 py-1 rounded-sm border border-[#26262B] text-[9px] font-mono uppercase tracking-[0.12em] text-[#8A8A93] hover:text-[#E0E0E0] hover:border-[#3F3F47] inline-flex items-center gap-1"
                >
                  <Eraser size={10} /> Reset
                </button>
                <button
                  type="button"
                  data-testid="cfm-shares-scanner-run"
                  onClick={runScanner}
                  disabled={loading}
                  className="px-3 py-1 rounded-sm border border-[#22D3CC]/40 bg-[#22D3CC]/10 text-[10px] font-mono uppercase tracking-[0.14em] text-[#22D3CC] hover:bg-[#22D3CC]/20 disabled:opacity-40 inline-flex items-center gap-1"
                >
                  <Play size={10} /> {loading ? "Scanning…" : "Run CFM Shares Scanner"}
                </button>
              </div>
            </div>

            <FriendlyScannerForm
              module="CFM_CC_SHARES"
              body={body}
              setBody={setBody}
              parseErr={parseErr}
            />

            <details
              data-testid="cfm-shares-scanner-advanced-payload"
              className="border border-[#26262B] rounded-sm"
            >
              <summary
                data-testid="cfm-shares-scanner-advanced-summary"
                className="cursor-pointer px-2 py-1 text-[10px] font-mono uppercase tracking-[0.14em] text-[#8A8A93] hover:text-[#E0E0E0]"
              >
                Advanced / Developer payload (raw JSON)
              </summary>
              <textarea
                data-testid="cfm-shares-scanner-payload"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                spellCheck={false}
                className="w-full bg-[#08080A] border-t border-[#26262B] p-2 text-[10px] font-mono text-[#E0E0E0] focus:outline-none focus:border-[#22D3CC]/50 leading-relaxed"
                rows={8}
              />
            </details>

            {parseErr && (
              <div
                data-testid="cfm-shares-scanner-parse-error"
                className="mt-2 flex items-start gap-2 text-[10px] font-mono text-[#FF3366] border border-[#FF3366]/30 bg-[#FF3366]/5 px-2 py-1.5 rounded-sm"
              >
                <AlertTriangle size={11} className="shrink-0 mt-0.5" />
                <span>{parseErr}</span>
              </div>
            )}
          </div>

          {/* ── Result render ───────────────────────────────────── */}
          {loading && (
            <div
              data-testid="cfm-shares-scanner-loading"
              className="px-4 py-3 text-[10px] font-mono text-[#5F5F66]"
            >
              Running CFM-CC-SHARES scanner…
            </div>
          )}

          {err && !loading && (
            <div
              data-testid="cfm-shares-scanner-error"
              className="px-4 py-3 flex items-start gap-2 text-[10px] font-mono text-[#FF3366]"
            >
              <AlertTriangle size={11} className="shrink-0 mt-0.5" />
              <span>{err}</span>
            </div>
          )}

          {!loading && !err && !result && (
            <div
              data-testid="cfm-shares-scanner-empty"
              className="px-4 py-3 text-[10px] font-mono text-[#5F5F66]"
            >
              No scan run yet. Edit the request body above and click
              Run CFM Shares Scanner.
            </div>
          )}

          {result && !loading && !err && (
            <ScannerResult result={result} onClear={clearResult} lastRequest={lastRequest} />
          )}
        </div>
      )}
    </div>
  );
}

function Metric({ label, value, testid }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <span
      data-testid={testid}
      className="inline-flex items-center gap-1 text-[#E0E0E0]"
    >
      <span className="text-[#5F5F66] uppercase tracking-[0.12em]">
        {label}
      </span>
      <span className="font-bold">{String(value)}</span>
    </span>
  );
}

function ScannerResult({ result, onClear, lastRequest }) {
  const counts = result.counts || {};
  return (
    <div
      data-testid="cfm-shares-scanner-result"
      className="px-4 py-3"
    >
      <div className="flex items-center gap-2 flex-wrap mb-3 text-[10px] font-mono">
        <CheckCircle2 size={11} className="text-[#00FF88]" />
        <span
          data-testid="cfm-shares-scanner-result-version"
          className="text-[#8A8A93] uppercase tracking-[0.12em]"
        >
          {result.scanner_version}
        </span>
        <span
          data-testid="cfm-shares-scanner-result-module-id"
          className="px-2 py-0.5 rounded-sm border border-[#22D3CC]/40 bg-[#22D3CC]/10 text-[9px] uppercase tracking-[0.14em] text-[#22D3CC]"
        >
          {result.module_id}
        </span>
        <span
          data-testid="cfm-shares-scanner-result-total"
          className="text-[#E0E0E0]"
        >
          {result.total_symbols} symbols scanned
        </span>
        <button
          type="button"
          data-testid="cfm-shares-scanner-result-clear"
          onClick={onClear}
          className="ml-auto text-[#5F5F66] hover:text-[#E0E0E0] uppercase tracking-[0.12em]"
        >
          Clear
        </button>
        <ScannerResultActions
          result={result}
          request={lastRequest}
          endpoint={SCANNER_ENDPOINT}
          sourcePanel="CFMSharesScannerPanel"
          moduleId={result.module_id || "CFM_CC_SHARES"}
          testidPrefix="cfm-shares-scanner-result"
        />
      </div>

      <div className="flex flex-wrap gap-3 text-[10px] font-mono mb-3">
        {COUNT_KEYS.map(k => (
          <span
            key={k}
            data-testid={`cfm-shares-scanner-result-count-${k}`}
            className="flex items-center gap-1 text-[#8A8A93]"
          >
            <span className="uppercase tracking-[0.12em]">{k}</span>
            <span className="font-bold text-[#E0E0E0]">{counts[k] ?? 0}</span>
          </span>
        ))}
      </div>

      {Array.isArray(result.warnings) && result.warnings.length > 0 && (
        <div className="mb-3">
          <ScannerWarnings
            warnings={result.warnings}
            testid="cfm-shares-scanner-result-warnings"
          />
        </div>
      )}

      <ul className="space-y-2">
        {(result.results || []).map(rec => (
          <StrategyResultCard
            key={rec.symbol}
            rec={rec}
            moduleLabel="CFM-CC-SHARES"
            accent={{
              border: "border-[#22D3CC]/30",
              glow:   "bg-[#22D3CC]/5",
              text:   "text-[#22D3CC]",
            }}
            testidPrefix="cfm-shares-scanner-result-row"
          >
            <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-[10px] font-mono">
              <Metric label="Basis adj" value={rec.share_adjusted_basis}
                       testid={`cfm-shares-scanner-result-row-basis-${rec.symbol}`} />
              <Metric label="Exit @" value={rec.assignment_exit_price}
                       testid={`cfm-shares-scanner-result-row-exit-${rec.symbol}`} />
              <Metric label="P&L/sh" value={rec.assignment_pnl_per_share}
                       testid={`cfm-shares-scanner-result-row-pnl-${rec.symbol}`} />
              <Metric label="Cushion %" value={rec.premium_cushion_pct}
                       testid={`cfm-shares-scanner-result-row-cushion-${rec.symbol}`} />
              <Metric label="Extrinsic" value={rec.extrinsic_value}
                       testid={`cfm-shares-scanner-result-row-extrinsic-${rec.symbol}`} />
            </div>
          </StrategyResultCard>
        ))}
      </ul>
    </div>
  );
}
